import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, RefreshCcw, Download, FileText, User } from 'lucide-react';
import { toast } from 'sonner';
import { useStaffAuth } from '../../context/StaffAuthContext';
import { apiErrorMessage } from '../../lib/apiError';
import { Button } from '../../components/ui/button';

export default function CrmApplications() {
  const { staff, authHeader, API, token } = useStaffAuth();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/crm/applications`, { headers: authHeader() });
      setApps(res.data.applications || []);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not load applications'));
    } finally {
      setLoading(false);
    }
  }, [API, authHeader]);

  useEffect(() => { load(); }, [load]);

  const download = async (a) => {
    try {
      const res = await axios.get(`${API}/crm/applications/${a.id}/cv`, {
        headers: authHeader(), responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = a.cv?.filename || 'cv';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not download CV'));
    }
  };

  if (staff.role === 'rm') {
    return (
      <div className="p-10 max-w-[900px] mx-auto">
        <div className="bg-white rounded-2xl border border-[#0B3B2E]/10 p-10 text-center">
          <User className="w-8 h-8 mx-auto text-[#0B3B2E]/40" />
          <h1 className="mt-4 font-serif text-[24px] text-[#0B3B2E]">Admin & Manager only</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#0B3B2E]/70 font-medium">CRM</p>
          <h1 className="mt-2 font-serif text-[32px] lg:text-[40px] leading-tight text-[#0B3B2E]">Applications</h1>
          <p className="mt-1 text-[13px] text-[#0E1F1A]/60">Candidate submissions from the Open roles form.</p>
        </div>
        <Button onClick={load} variant="outline"
          className="h-10 rounded-full border-[#0B3B2E]/25 bg-transparent text-[#0B3B2E] hover:bg-[#0B3B2E]/5">
          <RefreshCcw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </div>

      <div className="mt-6 bg-white rounded-2xl border border-[#0B3B2E]/10 overflow-hidden">
        {loading ? (
          <div className="p-16 flex items-center justify-center text-[#0B3B2E]">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : apps.length === 0 ? (
          <div className="p-16 text-center text-[#0E1F1A]/55 text-[14px]">No applications yet.</div>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="bg-[#0B3B2E]/5 text-[#0B3B2E]">
              <tr>
                <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider font-medium">Candidate</th>
                <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider font-medium">Role</th>
                <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider font-medium">Note</th>
                <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider font-medium">CV</th>
                <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider font-medium">Received</th>
              </tr>
            </thead>
            <tbody>
              {apps.map((a) => (
                <tr key={a.id} className="border-t border-[#0B3B2E]/8">
                  <td className="px-4 py-3 align-top">
                    <p className="font-medium text-[#0B3B2E]">{a.name}</p>
                    <p className="text-[12px] text-[#0E1F1A]/65">{a.email}</p>
                    {a.phone && <p className="text-[11px] text-[#0E1F1A]/55">{a.phone}</p>}
                  </td>
                  <td className="px-4 py-3 align-top text-[#0E1F1A]/85">{a.role || '—'}</td>
                  <td className="px-4 py-3 align-top text-[#0E1F1A]/70 max-w-[320px]">
                    <p className="line-clamp-3">{a.message || '—'}</p>
                  </td>
                  <td className="px-4 py-3 align-top">
                    {a.cv ? (
                      <button onClick={() => download(a)}
                        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-[#0B3B2E] text-[#F5F1E8] text-[12px] font-medium hover:bg-[#12503F]">
                        <Download className="w-3.5 h-3.5" /> {a.cv.filename}
                      </button>
                    ) : <span className="text-[#0E1F1A]/45">—</span>}
                  </td>
                  <td className="px-4 py-3 align-top text-[#0E1F1A]/55 whitespace-nowrap">
                    {a.created_at ? new Date(a.created_at).toLocaleDateString('en-GB') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
